import { useState } from 'react';
import { Header } from '../components/header';
import { useLanguage } from '../context/language-context';
import { Bell, CheckCircle, AlertTriangle, Clock, Calendar, X } from 'lucide-react';
import { Button } from '../components/ui/button';

interface Notification {
  id: number;
  title: string;
  title_ar: string;
  message: string;
  message_ar: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: string;
  isRead: boolean;
}

export default function NotificationsPage() {
  const { language } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      title: 'Inspection Assigned',
      title_ar: 'تم تعيين التفتيش',
      message: 'You have been assigned a new inspection for MARINA HEIGHTS HOTEL',
      message_ar: 'تم تعيينك لعملية تفتيش جديدة لفندق مارينا هايتس',
      type: 'info',
      timestamp: '2025-03-11T09:30:00',
      isRead: false,
    },
    {
      id: 2,
      title: 'Inspection Completed',
      title_ar: 'اكتمل التفتيش',
      message: 'Your inspection report for A CONSTRUCTIONS L.L.C has been submitted successfully',
      message_ar: 'تم تقديم تقرير التفتيش الخاص بك لشركة البناء ذ.م.م بنجاح',
      type: 'success',
      timestamp: '2025-03-10T14:45:00',
      isRead: true,
    },
    {
      id: 3,
      title: 'Deadline Approaching',
      title_ar: 'اقتراب الموعد النهائي',
      message: 'Inspection for AL NAHDA RETAIL CENTER is due in 24 hours',
      message_ar: 'موعد التفتيش لمركز النهضة للتجزئة خلال 24 ساعة',
      type: 'warning',
      timestamp: '2025-03-11T08:15:00',
      isRead: false,
    },
    {
      id: 4,
      title: 'System Maintenance',
      title_ar: 'صيانة النظام',
      message: 'The system will be under maintenance on March 15 from 2 AM to 4 AM',
      message_ar: 'سيخضع النظام للصيانة في 15 مارس من الساعة 2 صباحًا حتى 4 صباحًا',
      type: 'info',
      timestamp: '2025-03-09T11:00:00',
      isRead: true,
    },
    {
      id: 5,
      title: 'Inspection Rejected',
      title_ar: 'تم رفض التفتيش',
      message: 'Your inspection report for DUBAI MALL STORE has been rejected. Please review and resubmit.',
      message_ar: 'تم رفض تقرير التفتيش الخاص بمتجر دبي مول. يرجى المراجعة وإعادة التقديم.',
      type: 'error',
      timestamp: '2025-03-08T16:20:00',
      isRead: true,
    },
  ]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);

    if (diffMins < 60) {
      return language === 'en' ? `${diffMins} minutes ago` : `منذ ${diffMins} دقيقة`;
    } else if (diffHours < 24) {
      return language === 'en' ? `${diffHours} hours ago` : `منذ ${diffHours} ساعة`;
    } else if (diffDays < 7) {
      return language === 'en' ? `${diffDays} days ago` : `منذ ${diffDays} يوم`;
    } else {
      return date.toLocaleDateString(language === 'en' ? 'en-US' : 'ar-AE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'error':
        return <X className="w-5 h-5 text-red-500" />;
      case 'info':
      default:
        return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  const markAsRead = (id: number) => {
    setNotifications(
      notifications.map((notification) =>
        notification.id === id ? { ...notification, isRead: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({ ...notification, isRead: true }))
    );
  };

  const unreadCount = notifications.filter((notification) => !notification.isRead).length;

  return (
    <div
      className="flex flex-col min-h-screen"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Header
        title={language === 'en' ? 'Notifications' : 'الإشعارات'}
        isRTL={language === 'ar'}
      />

      <div className="p-4 flex-1 overflow-y-auto pb-20">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">
            {language === 'en'
              ? `Notifications (${unreadCount} unread)`
              : `الإشعارات (${unreadCount} غير مقروءة)`}
          </h2>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={markAllAsRead}
            >
              {language === 'en' ? 'Mark all as read' : 'تعيين الكل كمقروء'}
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`border rounded-lg p-4 ${
                  notification.isRead ? 'bg-white' : 'bg-blue-50'
                }`}
                onClick={() => markAsRead(notification.id)}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">{getTypeIcon(notification.type)}</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium">
                        {language === 'en'
                          ? notification.title
                          : notification.title_ar}
                      </h3>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                        {formatDate(notification.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {language === 'en'
                        ? notification.message
                        : notification.message_ar}
                    </p>
                  </div>
                </div>
                {!notification.isRead && (
                  <div className="flex justify-end mt-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-700">
                {language === 'en'
                  ? 'No notifications'
                  : 'لا توجد إشعارات'}
              </h3>
              <p className="text-sm text-gray-500">
                {language === 'en'
                  ? "You're all caught up!"
                  : 'أنت على اطلاع بكل شيء!'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}